<template>
  <div>
    <v-card-text>
      <v-alert v-if="errors.length" type="error" class="rounded-lg" dismissible @input="closeAlert">
        <div v-for="(error, index) in errors" :key="index">{{ error }}</div>
      </v-alert>
      <v-alert v-if="successes.length" type="success" class="rounded-lg " dismissible @input="closeAlert">
        <div v-for="(success, index) in successes" :key="index">{{ success }}</div>
      </v-alert>
      <v-form ref="groupForm" :readonly="!editMode">
        <v-text-field
          v-model="formGroup.name"
          v-bind="groupFieldsProperties('name')"
        />
        <v-textarea
          v-model="formGroup.description"
          v-bind="groupFieldsProperties('description')"
        />
      </v-form>
    </v-card-text>
    <v-divider />
    <v-card-text class="d-flex justify-end">
      <div v-if="editMode">
        <v-btn x-large color="warning" class="ml-4" @click="cancelEdit()">
          <v-icon class="mr-3">mdi-broom</v-icon>
          {{ getLabel("cancel") }}
        </v-btn>
        <v-btn x-large color="success" class="ml-4" @click="saveGroup()">
          <v-icon class="mr-3">mdi-content-save</v-icon>
          {{ getLabel("save") }}
        </v-btn>
      </div>
      <div v-else>
        <v-btn x-large class="mr-3" dark color="primary" @click="showDialog()">
          <v-icon class="mr-3">mdi-delete</v-icon>
          {{ getLabel("delete") }}
        </v-btn>
        <v-btn color="grey" dark x-large @click="startEdit()">
          <v-icon class="mr-1">mdi-pencil</v-icon> {{ getLabel("edit") }}
        </v-btn>
      </div>
    </v-card-text>
    <v-divider />
    <!-- DELETE group dialog -->
    <v-dialog
      v-model="dialog"
      min-width="400"
      max-width="450"
    >
      <v-card>
        <v-sheet color="red">
          <v-card-title class="text-h5">
            <v-row>
              <v-col cols="2" class="d-flex justify-center">
                <v-icon
                  x-large
                  color="white"
                >
                  mdi-alert-outline
                </v-icon>
              </v-col>
              <v-col class="pl-0">
                <span class="white--text text-center">
                  {{ getLabel("delete_group_question") }}
                </span>
              </v-col>
            </v-row>
          </v-card-title>
        </v-sheet>
        <v-card-text class="pt-4">
          <p class="ma-0">
            {{ getLabel("name") + ": " }}
            {{ formGroup.name }}
          </p>
          <p class="ma-0">
            {{ getLabel("description") + ": " }}
            {{ formGroup.description }}
          </p>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pt-4">
          <v-spacer />
          <v-btn
            class="mb-2"
            color="red darken-1"
            dark
            large
            @click="dialog = false"
          >
            <v-icon class="mr-2">mdi-close-thick</v-icon>

            {{ getLabel("no") }}
          </v-btn>
          <v-btn
            class="mb-2 ml-2"

            color="green darken-1"
            large
            dark
            @click="deleteGroup()"
          >
            <v-icon class="mr-2">mdi-check-bold</v-icon>

            {{ getLabel("yes") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api';
import { useMutation } from '@vue/apollo-composable';
import { useRouter } from 'vue2-helpers/vue-router';

import { getGraphqlSchema } from '../../graphql/index';
import { groupFieldsProperties } from '@/utils/forms';
import { useMainStore } from '@/store/index';
import { EventBus, getLabel } from '@/utils/index';

export default {
  setup (props, context) {
    const store = useMainStore();

    const router = useRouter();

    const errors = ref([]);

    const successes = ref([]);

    const closeAlert = () => {
      errors.value = [];
      successes.value = [];
    };

    const dialog = ref(false);

    const showDialog = () => {
      dialog.value = true;
    };

    const { roles } = store;

    const editMode = ref(false);

    const startEdit = () => {
      editMode.value = true;
    };

    const formGroup = ref({ ...store.group });

    // Update Group
    const { mutate: mutationUpdateGroup, error: updateMutationMessageError, onDone: onDoneUpdate, onError: onErrorUpdate } = useMutation(getGraphqlSchema('updateGroup'), () => ({
      variables: {
        uuid: formGroup.value.uuid,
        name: formGroup.value.name,
        description: formGroup.value.description,
        roles: formGroup.value.roles
      }
    }));

    onErrorUpdate(() => {
      store.loading = false;

      errors.value = [updateMutationMessageError.value.message];
      successes.value = [];
    });

    onDoneUpdate(result => {
      store.loading = false;

      editMode.value = false;

      // change only the attributes that have been saved
      store.group = { ...store.group, ...result.data.updateGroup };

      EventBus.$emit('update-group', {
        uuid: store.group.uuid,
        name: store.group.name
      });
      errors.value = [];

      successes.value = [getLabel('group_updated')];
    });

    const saveGroup = () => {
      if (context.refs.groupForm.validate()) {
        store.loading = true;
        mutationUpdateGroup();
      } else {
        successes.value = [];
        errors.value = [getLabel('check_fields')];
      }
    };

    const cancelEdit = () => {
      editMode.value = false;
      formGroup.value = { ...store.group };
    };

    // Mutation to DELETE Group
    const { mutate: mutationDeleteGroup, onDone: onDoneDelete, onError: onErrorDelete } = useMutation(getGraphqlSchema('deleteGroup'), () => ({ variables: { uuid: formGroup.value.uuid } }));

    onErrorDelete(error => {
      store.loading = false;

      // when the deleteCollaborator mutation returns an error

      EventBus.$emit('show-notifications', [{
        message: error.message,
        type: 'error'
      }]);
    });

    onDoneDelete(result => {
      store.loading = false;
      if (result.data.collaborator_delete_group === true) {
        EventBus.$emit('group-delete', store.group.uuid);

        // clean store group
        store.group = null;

        EventBus.$emit('show-notifications', [{
          message: getLabel('group_deleted'),
          type: 'success'
        }]);

        router.push({ name: 'groupsDashboard' });
      }
    });

    const deleteGroup = () => {
      dialog.value = false;
      store.loading = true;
      mutationDeleteGroup();
    };

    return {
      getLabel,
      groupFieldsProperties,
      errors,
      successes,
      closeAlert,
      dialog,
      showDialog,
      editMode,
      roles,
      startEdit,
      formGroup,
      saveGroup,
      cancelEdit,
      deleteGroup
    };
  }
};

</script>
